import React, { useMemo, useRef, useState } from 'react';
import { Wrapper, Header, Footer } from '../general_components/forms/Form';
import { IosToggle } from '../general_components/content_management/reusableItems/iosToggle';
import {
    canSeeLoadRates,
    retrieveMainBoardCustomizations,
    can,
    updateLocalMainBoardCustomizations,
} from '../supplementary_components/userDataDelegate';
import { API_ROOT, sendRequest } from '../supplementary_components/requests';
import { addNotification } from '../supplementary_components/store';
import { useBoardStore } from './boardStore';
import {
    DEFAULT_BOARD_STATISTICS,
    PLANNER_COLUMN_CATALOG,
    buildPlannerColumnAccessOpts,
    columnsForBoard,
    followsWeeklyOwnerPayoutFromCompany,
    getVisiblePlannerColumnCatalog,
    normalizeBoardStatistics,
    normalizePlannerColumns,
    normalizeShowDriverPayoutGross,
} from './plannerBoardSettings';
import styles from './mainBoardSettingsPopup.module.scss';

export { PLANNER_COLUMN_CATALOG, DEFAULT_BOARD_STATISTICS };

const STAT_OPTIONS = [
    { key: 'activeTrucks', label: 'Active Trucks' },
    { key: 'openTrucks', label: 'Open Trucks' },
    { key: 'atHomeTrucks', label: 'At Home Trucks' },
    { key: 'totalGross', label: 'Total Gross', requiresRates: true },
    { key: 'averageGross', label: 'Average Gross', requiresRates: true },
    { key: 'averageRpm', label: 'Average RPM', requiresRates: true },
];

/**
 * Resolve insert index + absolute marker position from pointer vs chip edges.
 * Uses X+Y so wrapped rows pick the correct slot. Marker height stays chip-sized.
 * Returns insertIndex: null when reorder would land in the same slot (keep sticky marker).
 */
function resolveDropFromPointer(stripEl, clientX, clientY, fromIndex) {
    if (!stripEl) return { insertIndex: null, markerLeft: null, markerTop: null };
    const stripRect = stripEl.getBoundingClientRect();
    const chips = [...stripEl.querySelectorAll('[data-column-chip="true"]')];
    if (chips.length === 0) {
        return { insertIndex: 1, markerLeft: 12, markerTop: 12 };
    }

    let best = { insertIndex: chips.length, dist: Infinity, markerLeft: 0, markerTop: 0 };
    for (let i = 0; i < chips.length; i++) {
        const rect = chips[i].getBoundingClientRect();
        const midY = rect.top + rect.height / 2;
        const top = rect.top - stripRect.top;
        const distLeft = Math.hypot(clientX - rect.left, clientY - midY);
        const distRight = Math.hypot(clientX - rect.right, clientY - midY);
        if (distLeft < best.dist) {
            best = {
                insertIndex: i,
                dist: distLeft,
                markerLeft: rect.left - stripRect.left - 4,
                markerTop: top,
            };
        }
        if (distRight < best.dist) {
            best = {
                insertIndex: i + 1,
                dist: distRight,
                markerLeft: rect.right - stripRect.left + 4,
                markerTop: top,
            };
        }
    }

    const insertIndex = Math.max(1, best.insertIndex);

    if (
        fromIndex != null &&
        (insertIndex === fromIndex || insertIndex === fromIndex + 1)
    ) {
        return { insertIndex: null, markerLeft: null, markerTop: null };
    }

    let markerLeft = best.markerLeft;
    let markerTop = best.markerTop;
    if (insertIndex !== best.insertIndex) {
        const truck = chips[0].getBoundingClientRect();
        markerLeft = truck.right - stripRect.left + 4;
        markerTop = truck.top - stripRect.top;
    } else if (insertIndex >= chips.length) {
        const last = chips[chips.length - 1].getBoundingClientRect();
        markerLeft = last.right - stripRect.left + 4;
        markerTop = last.top - stripRect.top;
    } else {
        const chip = chips[insertIndex].getBoundingClientRect();
        markerLeft = chip.left - stripRect.left - 4;
        markerTop = chip.top - stripRect.top;
    }

    return { insertIndex, markerLeft, markerTop };
}

/**
 * Main Board Settings popup (Planner Mode prefs). Save PATCHes + applies; Close discards.
 */
export const MainBoardSettingsPopup = ({ closePopup }) => {
    const hydratePlannerUiSettings = useBoardStore((s) => s.hydratePlannerUiSettings);
    const userIsUpdater = !can('DRIVER_PAYOUTS', 'VIEW');
    const userCanSeeRates = canSeeLoadRates();
    const followsWeeklyOwnerPayout = followsWeeklyOwnerPayoutFromCompany();
    const accessOpts = useMemo(
        () => buildPlannerColumnAccessOpts({ userCanSeeRates, userIsUpdater }),
        [userCanSeeRates, userIsUpdater]
    );
    const columnCatalog = useMemo(
        () => getVisiblePlannerColumnCatalog(accessOpts),
        [accessOpts]
    );

    const initialPrefs = useMemo(() => {
        const prefs = retrieveMainBoardCustomizations();
        return {
            horizontalScrollTimeline: prefs.horizontalScrollTimeline !== false,
            activeColumns: columnsForBoard(prefs.plannerColumns, accessOpts),
            statistics: normalizeBoardStatistics(prefs.boardStatistics, { userCanSeeRates }),
            showDriverPayoutGross: normalizeShowDriverPayoutGross(prefs.showDriverPayoutGross),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const headerStripRef = useRef(null);
    const lastDropRef = useRef({ insertIndex: null, markerLeft: null, markerTop: null });

    const [horizontalScrollTimeline, setHorizontalScrollTimeline] = useState(
        initialPrefs.horizontalScrollTimeline
    );
    const [activeColumns, setActiveColumns] = useState(initialPrefs.activeColumns);
    const [showDriverPayoutGross, setShowDriverPayoutGross] = useState(
        initialPrefs.showDriverPayoutGross
    );
    const [statistics, setStatistics] = useState(() =>
        normalizeBoardStatistics(initialPrefs.statistics, { userCanSeeRates })
    );
    const [dragPayload, setDragPayload] = useState(null);
    const [dropInsertIndex, setDropInsertIndex] = useState(null);
    const [markerLeft, setMarkerLeft] = useState(null);
    const [markerTop, setMarkerTop] = useState(null);
    const [saving, setSaving] = useState(false);

    const visibleStatOptions = useMemo(
        () => STAT_OPTIONS.filter((opt) => !(opt.requiresRates && !userCanSeeRates)),
        [userCanSeeRates]
    );

    const catalogByName = useMemo(() => {
        const map = {};
        for (const col of columnCatalog) map[col.name] = col;
        return map;
    }, [columnCatalog]);

    const setStat = (key, value) => {
        setStatistics((prev) => ({ ...prev, [key]: value }));
    };

    const clearDropIndicator = () => {
        lastDropRef.current = { insertIndex: null, markerLeft: null, markerTop: null };
        setDropInsertIndex(null);
        setMarkerLeft(null);
        setMarkerTop(null);
    };

    const commitDropIndicator = (insertIndex, left, top) => {
        lastDropRef.current = { insertIndex, markerLeft: left, markerTop: top };
        setDropInsertIndex(insertIndex);
        setMarkerLeft(left);
        setMarkerTop(top);
    };

    const onPaletteDragStart = (e, columnName) => {
        if (activeColumns.includes(columnName)) {
            e.preventDefault();
            return;
        }
        clearDropIndicator();
        setDragPayload({ source: 'palette', name: columnName });
        e.dataTransfer.effectAllowed = 'copy';
        e.dataTransfer.setData('text/plain', columnName);
    };

    const onActiveDragStart = (e, columnName, index) => {
        if (catalogByName[columnName]?.locked) {
            e.preventDefault();
            return;
        }
        clearDropIndicator();
        setDragPayload({ source: 'active', name: columnName, fromIndex: index });
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', columnName);
    };

    const onDragEnd = () => {
        setDragPayload(null);
        clearDropIndicator();
    };

    const insertActiveColumn = (name, atIndex) => {
        setActiveColumns((prev) => {
            if (prev.includes(name)) return prev;
            const next = [...prev];
            const safeIndex = Math.max(1, Math.min(atIndex ?? next.length, next.length));
            next.splice(safeIndex, 0, name);
            return next;
        });
    };

    const removeActiveColumn = (name) => {
        if (catalogByName[name]?.locked) return;
        setActiveColumns((prev) => prev.filter((n) => n !== name));
    };

    const reorderActiveColumn = (fromIndex, toIndex) => {
        setActiveColumns((prev) => {
            if (fromIndex < 1 || toIndex == null) return prev;
            const next = [...prev];
            const [item] = next.splice(fromIndex, 1);
            let safeTo = Math.max(1, Math.min(toIndex, next.length + 1));
            if (fromIndex < safeTo) safeTo -= 1;
            safeTo = Math.max(1, Math.min(safeTo, next.length));
            if (safeTo === fromIndex) return prev;
            next.splice(safeTo, 0, item);
            return next;
        });
    };

    const updateDropIndicator = (clientX, clientY) => {
        if (!dragPayload) return;
        const fromIndex = dragPayload.source === 'active' ? dragPayload.fromIndex : null;
        const resolved = resolveDropFromPointer(
            headerStripRef.current,
            clientX,
            clientY,
            fromIndex
        );
        if (resolved.insertIndex == null) return;
        if (resolved.insertIndex === lastDropRef.current.insertIndex) return;
        commitDropIndicator(resolved.insertIndex, resolved.markerLeft, resolved.markerTop);
    };

    const onHeaderStripDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        updateDropIndicator(e.clientX, e.clientY);
    };

    const onHeaderStripDragLeave = (e) => {
        const related = e.relatedTarget;
        if (related && e.currentTarget.contains(related)) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const { clientX, clientY } = e;
        if (
            clientX >= rect.left &&
            clientX <= rect.right &&
            clientY >= rect.top &&
            clientY <= rect.bottom
        ) {
            return;
        }
        clearDropIndicator();
    };

    const onHeaderStripDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!dragPayload) return;

        let insertIndex = lastDropRef.current.insertIndex;
        if (insertIndex == null) {
            const fromIndex = dragPayload.source === 'active' ? dragPayload.fromIndex : null;
            insertIndex = resolveDropFromPointer(
                headerStripRef.current,
                e.clientX,
                e.clientY,
                fromIndex
            ).insertIndex;
        }
        if (insertIndex == null) {
            onDragEnd();
            return;
        }

        if (dragPayload.source === 'palette') {
            insertActiveColumn(dragPayload.name, insertIndex);
        } else if (dragPayload.source === 'active') {
            reorderActiveColumn(dragPayload.fromIndex, insertIndex);
        }
        onDragEnd();
    };

    const onPaletteDrop = (e) => {
        e.preventDefault();
        if (dragPayload?.source === 'active') {
            removeActiveColumn(dragPayload.name);
        }
        onDragEnd();
    };

    const onSave = () => {
        if (saving) return;
        let columnsToSave = activeColumns;
        if (!accessOpts.truckStatusColumnEnabled) {
            const original = retrieveMainBoardCustomizations().plannerColumns;
            if (Array.isArray(original) && original.includes('truckStatus') && !columnsToSave.includes('truckStatus')) {
                const next = [...columnsToSave];
                const at = Math.max(1, Math.min(original.indexOf('truckStatus'), next.length));
                next.splice(at, 0, 'truckStatus');
                columnsToSave = next;
            }
        }
        const columns = normalizePlannerColumns(columnsToSave, accessOpts);
        const stats = normalizeBoardStatistics(statistics, { userCanSeeRates });
        const body = {
            horizontalScrollTimeline: horizontalScrollTimeline !== false,
            plannerColumns: columns,
            boardStatistics: stats,
            ...(userIsUpdater || !userCanSeeRates || followsWeeklyOwnerPayout
                ? {}
                : { showDriverPayoutGross: showDriverPayoutGross === true }),
        };
        setSaving(true);
        sendRequest(
            'PATCH',
            API_ROOT + 'employees/mainBoardCustomizations',
            null,
            (updated) => {
                updateLocalMainBoardCustomizations({
                    horizontalScrollTimeline: updated?.horizontalScrollTimeline ?? body.horizontalScrollTimeline,
                    plannerColumns: updated?.plannerColumns ?? body.plannerColumns,
                    boardStatistics: updated?.boardStatistics ?? body.boardStatistics,
                    ...(body.showDriverPayoutGross !== undefined
                        ? {
                              showDriverPayoutGross:
                                  updated?.showDriverPayoutGross ?? body.showDriverPayoutGross,
                          }
                        : {}),
                });
                hydratePlannerUiSettings();
                setSaving(false);
                addNotification({ message: 'Main board settings saved', type: 'success' });
                closePopup();
            },
            () => {
                setSaving(false);
                addNotification({ message: 'Failed to save main board settings', type: 'alert' });
            },
            body
        );
    };

    const showMarker = dropInsertIndex != null && markerLeft != null && markerTop != null;

    return (
        <Wrapper>
            <Header>
                <span>Main Board Settings</span>
            </Header>
            <div className={styles.content}>
                <section className={styles.section}>
                    <h3 className={styles.sectionTitle}>General Settings</h3>
                    <div className={styles.row}>
                        <div className={styles.rowLabelBlock}>
                            <span className={styles.rowLabel}>Timeline Horizontal Scroll</span>
                            {!horizontalScrollTimeline ? (
                                <span className={styles.hint}>
                                    Hold Shift while scrolling over the timeline to scroll horizontally
                                </span>
                            ) : null}
                        </div>
                        <IosToggle
                            state={horizontalScrollTimeline}
                            onChangeAction={setHorizontalScrollTimeline}
                        />
                    </div>
                    {!userIsUpdater && userCanSeeRates && !followsWeeklyOwnerPayout ? (
                        <div className={styles.row}>
                            <div className={styles.rowLabelBlock}>
                                <span className={styles.rowLabel}>Show Driver Payout Gross</span>
                                <span className={styles.hint}>
                                    Shows under Gross for Lease/Owner trucks in Planner Mode
                                </span>
                            </div>
                            <IosToggle
                                state={showDriverPayoutGross}
                                onChangeAction={setShowDriverPayoutGross}
                            />
                        </div>
                    ) : null}
                </section>

                <section className={styles.section}>
                    <h3 className={styles.sectionTitle}>Columns</h3>
                    <p className={styles.sectionHelp}>
                        Drag unused columns into the header to add them. Drag a column out (or onto the
                        palette) to remove it. Truck cannot be removed. Planner columns apply in Planner
                        Mode. Truck Status also shows on the regular board when it is in this list.
                    </p>

                    <div
                        className={styles.palette}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={onPaletteDrop}
                    >
                        {columnCatalog
                            .filter((col) => !col.locked)
                            .map((col) => {
                                const used = activeColumns.includes(col.name);
                                return (
                                    <div
                                        key={col.name}
                                        className={`${styles.chip} ${used ? styles.chipUsed : styles.chipAvailable}`}
                                        draggable={!used}
                                        onDragStart={(e) => onPaletteDragStart(e, col.name)}
                                        onDragEnd={onDragEnd}
                                        title={
                                            used
                                                ? 'Already on the board'
                                                : 'Drag onto the header to add'
                                        }
                                    >
                                        {col.label}
                                    </div>
                                );
                            })}
                    </div>

                    <div className={styles.headerPreviewLabel}>Board header</div>
                    <div
                        ref={headerStripRef}
                        className={styles.headerStrip}
                        onDragOver={onHeaderStripDragOver}
                        onDragLeave={onHeaderStripDragLeave}
                        onDrop={onHeaderStripDrop}
                    >
                        {activeColumns.map((name, index) => {
                            const col = catalogByName[name];
                            if (!col) return null;
                            return (
                                <div
                                    key={name}
                                    data-column-chip="true"
                                    className={`${styles.chip} ${styles.chipActive} ${
                                        col.locked ? styles.chipLocked : ''
                                    } ${dragPayload?.source === 'active' && dragPayload.fromIndex === index ? styles.chipDragging : ''}`}
                                    draggable={!col.locked}
                                    onDragStart={(e) => onActiveDragStart(e, name, index)}
                                    onDragEnd={onDragEnd}
                                >
                                    {col.label}
                                </div>
                            );
                        })}
                        {showMarker ? (
                            <div
                                className={styles.insertMarkerAbsolute}
                                style={{ left: `${markerLeft}px`, top: `${markerTop}px` }}
                                aria-hidden
                            />
                        ) : null}
                    </div>
                </section>

                <section className={styles.section}>
                    <h3 className={styles.sectionTitle}>Board Statistics</h3>
                    <div className={styles.statsList}>
                        {visibleStatOptions.map((opt) => (
                            <div key={opt.key} className={styles.row}>
                                <span className={styles.rowLabel}>{opt.label}</span>
                                <IosToggle
                                    state={!!statistics[opt.key]}
                                    onChangeAction={(checked) => setStat(opt.key, checked)}
                                />
                            </div>
                        ))}
                    </div>
                </section>
            </div>
            <Footer>
                <div className="non-selectable" onClick={() => !saving && closePopup()}>
                    Close
                </div>
                <div
                    className={`non-selectable ${saving ? 'disabled' : ''}`}
                    onClick={onSave}
                >
                    <input type="button" value={saving ? 'Saving…' : 'Save'} readOnly disabled={saving} />
                </div>
            </Footer>
        </Wrapper>
    );
};
